/*
 * Failure markers of `appFetch`, in a module of their own so a test that
 * mocks `appFetch` keeps the real check (the stores rely on it to tell a
 * refused call from a success).
 */

/**
 * What `appFetch` returns instead of a payload when the call did not succeed.
 * These are plain objects, so a bare `if (result)` reads them as success —
 * check with {@link isApiFailure} before trusting a response.
 */
export interface ApiFailure {
  unauthorized?: boolean;
  forbidden?: boolean;
  quotaExceeded?: boolean;
}

const FAILURE_MARKERS = [
  "unauthorized",
  "forbidden",
  "quotaExceeded",
] as const satisfies readonly (keyof ApiFailure)[];

/**
 * True when `appFetch` returned nothing usable: no body, or one of the failure
 * markers above. Callers that show a success message must gate on this —
 * otherwise a refused call (403, 402…) still reads as a win and reports
 * "undefined items sent".
 */
export function isApiFailure(result: unknown): result is ApiFailure | null {
  if (result === null || result === undefined) {
    return true;
  }

  if (typeof result !== "object") {
    return false;
  }

  return FAILURE_MARKERS.some((marker) => marker in result);
}
